import { Box, Typography, useTheme, Button, InputBase, TextField } from "@mui/material";
import { useSelector }  from "react-redux";
import { useState }     from "react";
import moment           from "moment";

import ScheduleIcon      from "@mui/icons-material/Schedule";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import FilterListIcon    from "@mui/icons-material/FilterList";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import ResponsiveDialog  from "components/responsiveDialog";
import ShiftAssignmentForm     from "../forms/shiftForms/shiftAssignmentForm";
import EditShiftAssignmentForm from "../forms/shiftForms/editShiftAssignmentForm";
import { useEmployeeShiftsList } from "api/hooks/humanResource";
import { requester }     from "api/pesabackend";

const PERIODS = ["TODAY", "THIS WEEK", "THIS MONTH", "CUSTOM"];

export default function EmployeeShiftAssignmentTable({ setIsSuccess, setIsFailed }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { employeeShiftList, isLoadingEmployeeShiftList, setFilter, setNewEmployeeShiftData, setEmployee } = useEmployeeShiftsList(1, business_branch_id);

  const [open,         setOpen]         = useState(false);
  const [openEdit,     setOpenEdit]     = useState(false);
  const [openDelete,   setOpenDelete]   = useState(false);
  const [employeeData, setEmployeeData] = useState("");
  const [search,       setSearch]       = useState("");
  const [showCustom,   setShowCustom]   = useState(false);
  const [activePeriod, setActivePeriod] = useState("THIS MONTH");
  const [startDate,    setStartDate]    = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate,      setEndDate]      = useState(moment().endOf("month").format("YYYY-MM-DD"));

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const handlePeriod = (val) => {
    setActivePeriod(val);
    if (val === "TODAY")      { setStartDate(moment().format("YYYY-MM-DD")); setEndDate(moment().format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS WEEK")  { setStartDate(moment().startOf("week").format("YYYY-MM-DD")); setEndDate(moment().endOf("week").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "THIS MONTH") { setStartDate(moment().startOf("month").format("YYYY-MM-DD")); setEndDate(moment().endOf("month").format("YYYY-MM-DD")); setShowCustom(false); }
    if (val === "CUSTOM")     { setShowCustom(true); }
  };

  const handleClickOpenEdit = async (id) => {
    const res = await requester.get(`/human_resource/employees/shifts/get/${id}`);
    setEmployeeData(res.data.data); setOpenEdit(true);
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/human_resource/employees/shifts/delete/${employeeData.id}`);
    if (res.data.status === 201) { setIsSuccess(true); setNewEmployeeShiftData(true); setOpenDelete(false); }
    if (res.data.status === 400) { setIsFailed(true); }
  };

  const columns = [
    { label: "Employee"             },
    { label: "Phone Number"         },
    { label: "Shift Assigned"       },
    { label: "Day Assigned"         },
    { label: "Actions", align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ScheduleIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Shift Assignments</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>{employeeShiftList.length} total assignments</Typography>
          </Box>
        </Box>
        <Button onClick={() => setOpen(true)} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Assign Shift
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem", p: "0.75rem 1rem", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "10px" }}>
        <FilterListIcon sx={{ fontSize: "16px", color: accent }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "0.75rem", height: "36px", backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f9f9f9", border: `1px solid ${border}`, borderRadius: "8px", flex: "1 1 180px", maxWidth: "220px" }}>
          <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
          <InputBase placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setEmployee(search); }} sx={{ flex: 1, fontSize: "13px", color: textPri }} />
          <Box onClick={() => setEmployee(search)} sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer" }}>Search</Box>
        </Box>
        <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
          {PERIODS.map((p) => (
            <Box key={p} onClick={() => handlePeriod(p)} sx={{ px: "0.65rem", py: "0.25rem", borderRadius: "20px", fontSize: "12px", fontWeight: 500, cursor: "pointer", border: `1px solid ${activePeriod === p ? accent : border}`, color: activePeriod === p ? accent : textSec, backgroundColor: activePeriod === p ? "rgba(29,158,117,0.1)" : "transparent", transition: "all 0.15s" }}>
              {p === "TODAY" ? "Today" : p === "THIS WEEK" ? "This week" : p === "THIS MONTH" ? "This month" : "Custom"}
            </Box>
          ))}
        </Box>
        {showCustom && (
          <>
            <TextField label="Start date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} inputProps={{ max: endDate }} sx={{ ...inputSx, width: "150px" }} />
            <TextField label="End date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} inputProps={{ min: startDate }} sx={{ ...inputSx, width: "150px" }} />
          </>
        )}
        <Button onClick={() => setFilter({ start: startDate, end: endDate })} sx={{ fontSize: "12px", fontWeight: 500, px: "1rem", py: "0.35rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>Apply</Button>
      </Box>

      <ReportTable columns={columns} rows={employeeShiftList} loading={isLoadingEmployeeShiftList}
        emptyMessage="No shift assignments found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.related_employee.first_name} {row.related_employee.last_name}</Typography></RCell>
            <RCell>{row.related_employee.phone_number}</RCell>
            <RCell>{row.related_shift.shift_name}</RCell>
            <RCell>{moment(row.day_assigned).format("LL")}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickOpenEdit(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                  <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                </Box>
                <Box onClick={async () => { const res = await requester.get(`/human_resource/employees/shifts/get/${row.id}`); setEmployeeData(res.data.data); setOpenDelete(true); }} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <ResponsiveDialog open={open} handleClose={() => setOpen(false)} dialogHeading="Assign Shift to Employee" negativeResponse="Cancel"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><ShiftAssignmentForm setOpen={setOpen} setNewEmployeeShiftData={setNewEmployeeShiftData} setIsSuccess={setIsSuccess} /></Box>}
      />
      <FixedSizeDialog open={openEdit} handleClose={() => setOpenEdit(false)} dialogHeading="Edit Shift Assignment" negativeResponse="Cancel" maxWidth="md"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><EditShiftAssignmentForm setOpen={setOpenEdit} setNewEmployeeShiftData={setNewEmployeeShiftData} setIsSuccess={setIsSuccess} employeeData={employeeData} /></Box>}
      />
      <FixedSizeDialog open={openDelete} handleClose={() => setOpenDelete(false)} dialogHeading="Delete this shift assignment?" negativeResponse="Cancel"
        positiveResponse="Yes, delete" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will permanently delete the shift assignment and cannot be undone.</Typography>
          </Box>
        }
      />
    </Box>
  );
}